"use client";

import React, { useEffect, useState, useMemo, useRef } from "react";
import type { FilterState } from "./components/SidebarFilter";
import { useTenant } from "@/src/contexts/TenantContext";
import type { Category, ComboV3, Coupon, OutletDetails, Product, Tenant } from "@/src/types/tenant";
import type { StorefrontConfig, TopOfferBannerConfig } from "@/src/types/storefront";
import { matchesCurator, matchesPriceRange } from "@/src/utils/filter/productFilter";
import { normalizeImageUrl } from "@/src/utils/url";
import { SECTION_REGISTRY, type ShopData } from "./sectionRegistry";
import { MoreNeighbors } from "./components/MoreNeighbors";
import { resolveRenderSections } from "@/src/config/defaultStorefront";
import { resolveThemeVars } from "@/src/config/theme";
import { useServingOutlet } from "@/src/hooks/catalog/useServingOutlet";
import { dedupeToUnique, narrowToOutlet } from "@/src/utils/products/groupCatalog";

interface ShopClientProps {
  prefix: string;
  tenant: Tenant;
  /** Primary outlet — drives the header, SEO and the server-rendered default. */
  outlet: OutletDetails | null;
  /** Every landing outlet, primary first. Empty/1-length for single-outlet stores. */
  outlets?: OutletDetails[];
  categories: Category[];
  coupons: Coupon[];
  /** Every outlet's coupons, keyed by outletId. Empty on an older portal-api. */
  couponsByOutlet?: Record<string, Coupon[]>;
  /** v3 combos (Mongo). Single source of truth — no legacy fallback. */
  combos: ComboV3[];
  products: Product[];
  /** See TenantLandingResponse.grouped — false means collapse before rendering. */
  grouped?: boolean;
  /** Seller's storefront customizer config; null → default layout. */
  storefront?: StorefrontConfig | null;
  /** When true, listen for live draft config from the CRM editor (iframe preview). */
  preview?: boolean;
}

function effectivePrice(p: Product): number {
  return p.pricing?.offerPrice ?? p.pricing?.sellingPrice ?? 0;
}

export default function ShopClient({
  prefix,
  tenant,
  outlet: outletProp,
  outlets: outletsProp,
  categories,
  coupons,
  couponsByOutlet = {},
  combos,
  products: rawProducts,
  grouped = false,
  storefront = null,
  preview = false,
}: ShopClientProps) {
  const { setTenant, setTopOfferBannerOffers } = useTenant();

  // Live preview: hold the draft config pushed by the CRM editor over postMessage.
  const [liveStorefront, setLiveStorefront] = useState<StorefrontConfig | null>(storefront);
  const appliedThemeKeys = useRef<string[]>([]);

  useEffect(() => {
    if (!preview) return;
    const applyTheme = (theme?: StorefrontConfig["theme"]) => {
      const vars = resolveThemeVars(theme);
      const root = document.documentElement;
      appliedThemeKeys.current.forEach((k) => {
        if (!(k in vars)) root.style.removeProperty(k);
      });
      Object.entries(vars).forEach(([k, v]) => root.style.setProperty(k, v));
      appliedThemeKeys.current = Object.keys(vars);
    };
    const onMessage = (e: MessageEvent) => {
      if (!/^https?:\/\/(localhost(:\d+)?|.*\.grablocal\.in|.*\.lvh\.me)$/.test(e.origin)) return;
      const data = e.data;
      if (!data || data.type !== "storefront-preview") return;
      const sf: StorefrontConfig | null = data.storefront ?? null;
      setLiveStorefront(sf);
      applyTheme(sf?.theme);
    };
    window.addEventListener("message", onMessage);
    // Signal the parent editor that we're ready to receive the initial draft.
    try {
      window.parent?.postMessage({ type: "storefront-preview-ready" }, "*");
    } catch {
      /* cross-origin parent: ignore */
    }
    return () => window.removeEventListener("message", onMessage);
  }, [preview]);

  const [filters, setFilters] = useState<FilterState>({
    category: "all",
    subcategory: "all",
    curators: [],
    priceRanges: [],
  });
  const outlet = outletProp ?? rawProducts[0]?.outlet ?? null;
  const outletId = outlet?.outletId || "";

  /* Fall back to the single primary outlet when the API predates `outlets[]`,
   * so a stale portal-api degrades to today's behaviour rather than an empty
   * storefront. */
  const outlets = useMemo(
    () => (outletsProp?.length ? outletsProp : outlet ? [outlet] : []),
    [outletsProp, outlet]
  );

  /* The one branch this buyer shops from. `outletId` (the primary outlet) is the
   * fallback, which is what the server renders. A branch carrying no products at
   * all is skipped — see nearestOutletId. */
  const servingOutletId = useServingOutlet(outlets, outletId);

  /* Memoized: this feeds a fetch effect's dependency list inside
   * CategoryFilterBar, and a fresh array each render would re-fetch forever. */
  const allOutletIds = useMemo(() => outlets.map((o) => o.outletId), [outlets]);

  const servingOutlet = useMemo(
    () => outlets.find((o) => o.outletId === servingOutletId) ?? outlet,
    [outlets, servingOutletId, outlet]
  );

  /* Every product the seller stocks, across all branches, each appearing once.
   * Named `products` so every section below — filters, deal-of-the-day, the
   * data bag — consumes the grouped list without knowing multi-outlet exists.
   *
   * portal-api's landing payload now arrives already collapsed (it asks the
   * product service for `unique=true` across the landing outlets), so on a
   * current stack this is a no-op. It stays because grouping is idempotent and
   * portal-api falls back to an un-collapsed per-outlet fetch when the batched
   * call fails — this is what keeps duplicate cards off the page in that window.
   *
   * This replaces narrowing to the nearest branch, which hid whatever that
   * branch does not stock: Chak & Go sells four things and a buyer beside its
   * second outlet saw three. Deliberately NOT keyed on `servingOutletId` —
   * the cart matches lines by `_id` alone and the serving outlet re-resolves on
   * every address change, so a location-dependent winner would reset the
   * quantity stepper mid-session. Single-outlet stores pass through untouched,
   * order included. */
  const products = useMemo(
    () => (grouped ? rawProducts : dedupeToUnique(rawProducts)),
    [grouped, rawProducts]
  );

  /* Combos are fetched across every outlet by (landing)/page.tsx and merged, the
   * same way portal-api merges products. Narrow them with the same rule, or a
   * Bangalore buyer is offered a bundle that ships from Mumbai. */
  const visibleCombos = useMemo(
    () => narrowToOutlet(combos, servingOutletId),
    [combos, servingOutletId]
  );

  /* Offers follow the branch the buyer is shopping. Falls back to `coupons` —
   * the primary outlet's — for an older portal-api with no keyed map, and for a
   * branch that has none of its own. */
  const visibleCoupons = useMemo(
    () => couponsByOutlet[servingOutletId] ?? coupons,
    [couponsByOutlet, servingOutletId, coupons]
  );

  useEffect(() => {
    setTenant({
      storeName: servingOutlet?.outletName || tenant.tenantName,
      logoUrl: normalizeImageUrl(tenant.additionalInfo?.logoUrl),
      outletId: servingOutletId,
      tenantId: tenant.tenantId != null ? String(tenant.tenantId) : undefined,
      prefix,
      minCartValue: servingOutlet?.minCartValue ?? null,
      city: servingOutlet?.city ?? null,
    });
    /* Re-runs when the buyer switches address. The header's delivery badge
     * (useOutletDelivery), the add-to-cart serviceability gate (useServiceArea)
     * and checkout's address step all read this context, and they must describe
     * the branch whose products are on screen — not the primary outlet. */
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [servingOutletId, servingOutlet]);

  // Keep header ticker in sync with the topOfferBanner section (includes live preview updates).
  useEffect(() => {
    const resolved = resolveRenderSections(preview ? liveStorefront : storefront);
    const topOffer = resolved.find(s => s.type === 'topOfferBanner');
    const offers = topOffer?.enabled ? (topOffer.config as TopOfferBannerConfig)?.offers : undefined;
    setTopOfferBannerOffers(offers?.length ? offers : undefined);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [liveStorefront]);

  // Prefer outlet name over tenant name for display
  const displayName = outlet?.outletName || tenant.tenantName;
  const businessBrief = tenant.additionalInfo?.businessBrief?.trim();
  const location = [outlet?.city, outlet?.state].filter(Boolean).join(", ");
  const subtitle = [businessBrief, location].filter(Boolean).join(" · ");

  // ── Derived filtered product list ─────────────────────────────────────────
  const filteredProducts = useMemo(() => {
    return products.filter((p) => {
      // Category. `filters.category` is a platform category id for flat catalogues,
      // or a department id (a top-level subcategory) for sellers whose nav is
      // category -> department -> leaf. Accept either so both shapes filter.
      if (filters.category !== "all") {
        // An explicit membership set wins: it comes from the canonical taxonomy,
        // which knows about categories portal-api's ids cannot express.
        const inGroup = filters.categoryProductIds
          ? filters.categoryProductIds.includes(String(p._id))
          : p.category?._id === filters.category ||
            p.subcategories?.some((sc) => String(sc._id) === filters.category);
        if (!inGroup) return false;
      }

      // Subcategory
      if (filters.subcategory !== "all") {
        const hasSubcat = p.subcategories?.some((sc) => sc._id === filters.subcategory);
        if (!hasSubcat) return false;
      }

      // Price ranges (any selected range must match — OR logic)
      if (filters.priceRanges.length > 0) {
        const price = effectivePrice(p);
        const matchesAnyPrice = filters.priceRanges.some((r) => matchesPriceRange(price, r));
        if (!matchesAnyPrice) return false;
      }

      // Curator's pick (all selected curators must match — AND logic)
      if (filters.curators.length > 0) {
        const matchesAllCurators = filters.curators.every((c) => matchesCurator(p, c));
        if (!matchesAllCurators) return false;
      }

      return true;
    });
  }, [products, filters]);

  // Pick the highest-discount product as deal-of-the-day / hero feature default.
  const dealProduct = useMemo(
    () =>
      [...products].sort((a, b) => {
        const discA = a.pricing?.mrp && a.pricing?.offerPrice ? a.pricing.mrp - a.pricing.offerPrice : 0;
        const discB = b.pricing?.mrp && b.pricing?.offerPrice ? b.pricing.mrp - b.pricing.offerPrice : 0;
        return discB - discA;
      })[0],
    [products]
  );

  // Shared data bag every section adapter reads from.
  const data: ShopData = useMemo(
    () => ({
      prefix,
      tenant,
      outlet,
      outletId,
      /* Undefined on purpose. The catalogue can now mix branches, and
       * OutletLocation's own `hasMultipleOutlets(products)` inspects the
       * rendered list — the only signal that stays correct after grouping. An
       * explicit boolean overrides it, and would either label every card in a
       * single-city store or none in a genuinely mixed grid. */
      multiOutlet: undefined,
      allOutletIds,
      servingOutlet,
      categories,
      coupons: visibleCoupons,
      combos: visibleCombos,
      products,
      allProducts: rawProducts,
      allCombos: combos,
      filteredProducts,
      filters,
      setFilters,
      displayName,
      subtitle,
      businessBrief: businessBrief || undefined,
      dealProduct,
      // Seller's configured category order — which departments open first.
      productPriority: storefront?.productPriority,
    }),
    [
      prefix,
      tenant,
      outlet,
      outletId,
      servingOutlet,
      allOutletIds,
      categories,
      visibleCoupons,
      visibleCombos,
      products,
      rawProducts,
      combos,
      filteredProducts,
      filters,
      displayName,
      subtitle,
      businessBrief,
      dealProduct,
      storefront?.productPriority,
    ]
  );

  // Resolve the ordered, visible sections (falls back to the default layout).
  // In preview mode, render the live draft pushed from the CRM editor.
  const sections = resolveRenderSections(preview ? liveStorefront : storefront);

  return (
    <div
      className="w-full"
      style={{ background: "var(--rev-white)", color: "var(--techno-black)" }}
    >
      <div className="gl-container">
        {sections.map((s) => {
          const Section = SECTION_REGISTRY[s.type];
          if (!Section) return null;
          return <Section key={s.id} data={data} config={s.config} />;
        })}

        {/* Always rendered just above the footer — not a seller-configurable
            section, so it can't be reordered, hidden, or removed in the CRM. */}
        <MoreNeighbors currentPrefix={prefix} />
      </div>
    </div>
  );
}
